const DEFAULT_DOWNLOAD_FILENAME = 'download'

function getHeader(headers, name) {
  if (!headers) return ''
  const lowerName = name.toLowerCase()
  const key = Object.keys(headers).find(headerName => headerName.toLowerCase() === lowerName)
  return key ? headers[key] : ''
}

function decodeFilename(value) {
  if (!value) return ''

  const rawValue = value
    .trim()
    .replace(/^UTF-8''/i, '')

  const unwrapped = unwrapBytesLiteral(rawValue)
  const normalized = unwrapped.value
    .replace(/^['"]|['"]$/g, '')
    .replace(/\\x/g, '%')

  try {
    return normalizeDecodedFilename(decodeURIComponent(normalized), unwrapped.isBytesLiteral)
  } catch (error) {
    try {
      return normalizeDecodedFilename(decodeURI(normalized), unwrapped.isBytesLiteral)
    } catch (decodeError) {
      return normalizeDecodedFilename(normalized, unwrapped.isBytesLiteral)
    }
  }
}

function unwrapBytesLiteral(value) {
  const match = value.match(/^b(['"])(.*)\1$/i)
  return match
    ? { value: match[2], isBytesLiteral: true }
    : { value, isBytesLiteral: false }
}

function normalizeDecodedFilename(filename, isBytesLiteral = false) {
  if (!filename) return ''

  const duplicateExtension = filename.match(/_([A-Za-z0-9]+)\.\1$/)
  if (isBytesLiteral && duplicateExtension) {
    return `${filename.slice(0, duplicateExtension.index)}.${duplicateExtension[1]}`
  }

  return filename
}

export function getDownloadFilename(response, fallbackFilename = DEFAULT_DOWNLOAD_FILENAME) {
  const disposition = getHeader(response && response.headers, 'content-disposition')
  if (!disposition) return fallbackFilename

  const encodedFilename = disposition.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i)
  if (encodedFilename && encodedFilename[1]) {
    return decodeFilename(encodedFilename[1]) || fallbackFilename
  }

  const plainFilename = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i)
  if (plainFilename && plainFilename[1]) {
    return decodeFilename(plainFilename[1]) || fallbackFilename
  }

  return fallbackFilename
}

export function isJsonBlobResponse(response) {
  const data = response && response.data
  const contentType = getHeader(response && response.headers, 'content-type')

  return data instanceof Blob && (
    data.type === 'application/json' ||
    contentType.indexOf('application/json') !== -1
  )
}

function readBlobAsText(blob) {
  if (blob && typeof blob.text === 'function') return blob.text()

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsText(blob)
  })
}

export async function parseBlobError(response) {
  if (!isJsonBlobResponse(response)) return null

  const text = await readBlobAsText(response.data)
  if (!text) return null

  try {
    return JSON.parse(text)
  } catch (error) {
    return { detail: text }
  }
}

export function downloadBlob(blob, filename = DEFAULT_DOWNLOAD_FILENAME) {
  const downloadData = blob instanceof Blob ? blob : new Blob([blob])
  const fileObjectUrl = window.URL.createObjectURL(downloadData)
  const link = document.createElement('a')

  link.href = fileObjectUrl
  link.style.display = 'none'
  link.download = filename || DEFAULT_DOWNLOAD_FILENAME
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(fileObjectUrl)
}

export async function downloadBlobResponse(response, options = {}) {
  const errorData = await parseBlobError(response)
  if (errorData) {
    const error = new Error(errorData.detail || errorData.message || '다운로드에 실패했습니다.')
    error.response = {
      status: response.status,
      headers: response.headers,
      data: errorData,
    }
    throw error
  }

  const blob = response.data instanceof Blob ? response.data : new Blob([response.data])
  const filename = getDownloadFilename(response, options.fallbackFilename)
  downloadBlob(blob, filename)
  return filename
}
